import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AdminService } from './admin.service';
import { GlossaryService, AnnotateMatch } from './glossary.service';
import { Report, Service } from './models';

interface Tally { key: string; count: number; }
interface ActivityEvent { type: string; slug: string; label: string; at: string; }
interface ActivitySummary {
  totalSubmissions: number;
  totalDownloads: number;
  totalServiceInterest: number;
  byReport: Tally[];
  byService: Tally[];
  byDay: Tally[];
  recent: ActivityEvent[];
  note: string;
}

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './admin.component.html'
})
export class AdminComponent implements OnInit 
{
  reports: Report[] = [];
  services: Service[] = [];
  editingReport: Report | null = null;
  editingService: Service | null = null;
  notice = '';
  error = '';

  reportStatuses = ['available', 'comingSoon', 'inProduction', 'retired'];
  groups = ['intelligence', 'education', 'convening', 'diagnostic', 'visibility'];

  //activity dashboard
  activity: ActivitySummary | null = null;

  //admin token, required by the API for writes once Site:AdminToken is set in production
	adminToken = '101b5fe0-5f4d-4a5f-9ea0-7884c69f6108';

  applyToken(): void {
    this.api.setToken(this.adminToken);
    this.load();
    this.loadActivity();
    this.flash(this.adminToken ? 'Token applied.' : 'Token cleared.');
  }

  //glossary surfacing tool
  glossaryInput = '';
  glossaryHtml: SafeHtml = '';
  glossaryMatches: AnnotateMatch[] = [];
  glossaryFirstOnly = true;
  glossaryBusy = false;
  glossaryCount = 0;

  selectedFile: File | null = null


  constructor(
    private api: AdminService,
    private glossary: GlossaryService,
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) {}

  ngOnInit(): void { this.load(); this.loadActivity(); }

  loadActivity(): void {
    this.http.get<ActivitySummary>('/api/activity/summary')
      .subscribe({ next: a => this.activity = a, error: () => {} });
  }

  surfaceTerms(): void {
    const text = this.glossaryInput.trim();
    if (!text) { this.glossaryHtml = ''; this.glossaryMatches = []; return; }
    this.glossaryBusy = true;
    this.glossary.annotate(text, this.glossaryFirstOnly).subscribe({
      next: r => {
        this.glossaryHtml = this.sanitizer.bypassSecurityTrustHtml(r.html);
        this.glossaryMatches = r.matches;
        this.glossaryCount = r.matches.length;
        this.glossaryBusy = false;
      },
      error: e => { this.glossaryBusy = false; this.fail(this.msg(e)); }
    });
  }

  clearGlossary(): void { this.glossaryInput = ''; this.glossaryHtml = ''; this.glossaryMatches = []; this.glossaryCount = 0; }

  load(): void {
    this.api.listReports().subscribe({ next: r => this.reports = r, error: e => this.fail(this.msg(e)) });
    this.api.listServices().subscribe({ next: s => this.services = s, error: e => this.fail(this.msg(e)) });
  }

  private flash(m: string): void { this.notice = m; this.error = ''; setTimeout(() => this.notice = '', 4000); }
  private fail(m: string): void { this.error = m; setTimeout(() => this.error = '', 6000); }
  private msg(e: unknown): string {
    const err = e as { error?: unknown };
    if (err && typeof err.error === 'string') return err.error;
    return 'Something went wrong. Check that the API is running on port 5099.';
  }

  splitLines(v: string): string[] { return v.split('\n').map(x => x.trim()).filter(x => x.length > 0); }

	//----- Reports -----
	onFileSelected(event: Event): void
	{
		const input = event.target as HTMLInputElement;

		if (!input.files || input.files.length === 0)
		{
			return;
		}

		const file = input.files[0];
		this.selectedFile = file;

	}
  newReport(): void {
    this.editingReport = {
      slug: '', title: '', descriptor: '', tagline: '', hook: '',
      status: 'comingSoon', featured: false, order: this.reports.length + 1,
      coverImage: '', thumbnailImage: '', file: null,
      findings: [], glossaryTerms: [], version: ''
    };
  }
  editReport(r: Report): void {
    this.editingReport = { ...r, findings: [...(r.findings || [])], glossaryTerms: [...(r.glossaryTerms || [])] };
  }
  cancelReport(): void { this.editingReport = null; }

  saveReport(): void
  {
	const r = this.editingReport;
	if (!r) return;
	if (!r.slug || !r.title) { this.fail('A slug and a title are required.'); return; }
	const exists = this.reports.some(x => x.slug === r.slug);
	const op = exists ? this.api.updateReport(r.slug, r) : this.api.createReport(r);
	op.subscribe({ next: () => { this.editingReport = null; this.load(); this.flash('Report saved.'); }, error: e => this.fail(this.msg(e)) });
  }

  publish(r: Report): void {
    this.api.publishReport(r.slug).subscribe({ next: () => { this.load(); this.flash(r.title + ' is now available.'); }, error: e => this.fail(this.msg(e)) });
  }
  retire(r: Report): void {
    this.api.retireReport(r.slug).subscribe({ next: () => { this.load(); this.flash(r.title + ' retired.'); }, error: e => this.fail(this.msg(e)) });
  }
  moveReport(i: number, dir: number): void {
    const j = i + dir;
    if (j < 0 || j >= this.reports.length) return;
    const arr = [...this.reports];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    this.reports = arr;
    this.api.reorderReports(arr.map(x => x.slug)).subscribe({ next: () => this.flash('Order saved.'), error: e => this.fail(this.msg(e)) });
  }

  // ----- services -----
  newService(): void {
    this.editingService = {
      slug: '', name: '', description: '', cta: 'Inquire', group: 'intelligence', status: 'active',
      pricing: 'inquiry', stripeLink: '', planningSession: false, order: this.services.length + 1
    };
  }
  editService(s: Service): void { this.editingService = { ...s }; }
  cancelService(): void { this.editingService = null; }
  saveService(): void {
    const s = this.editingService;
    if (!s) return;
    if (!s.slug || !s.name) { this.fail('A slug and a name are required.'); return; }
    const exists = this.services.some(x => x.slug === s.slug);
    const op = exists ? this.api.updateService(s.slug, s) : this.api.createService(s);
    op.subscribe({ next: () => { this.editingService = null; this.load(); this.flash('Service saved.'); }, error: e => this.fail(this.msg(e)) });
  }
  toggleService(s: Service): void {
    const next = s.status === 'active' ? 'hidden' : 'active';
    this.api.setServiceStatus(s.slug, next).subscribe({ next: () => { this.load(); this.flash(s.name + ' ' + (next === 'hidden' ? 'hidden' : 'shown') + '.'); }, error: e => this.fail(this.msg(e)) });
  }
  moveService(i: number, dir: number): void {
    const j = i + dir;
    if (j < 0 || j >= this.services.length) return;
    const arr = [...this.services];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    this.services = arr;
    this.api.reorderServices(arr.map(x => x.slug)).subscribe({ next: () => this.flash('Order saved.'), error: e => this.fail(this.msg(e)) });
  }
}
